import { z } from 'zod';
/**
 * A per-account feature flag.
 *
 * The CATALOGUE lives in code and the database holds only per-account
 * OVERRIDES. That split is what makes a flag a finite, reviewable set rather
 * than free text: a key is a union member, so a typo is a type error and every
 * consumer of `FEATURE_FLAGS` (the admin screen, the seed, a route gate) sees
 * the same list without a query. It also means a flag going public is a
 * one-line change to `defaultEnabled` below rather than a backfill across every
 * account row - and that turning it back off restores the pre-GA behaviour for
 * everyone except the accounts explicitly switched on, because their override
 * rows survive the default flip.
 *
 * Adding a flag: add an entry here, and nothing else. There is deliberately no
 * migration in that path - the stored `key` is a plain string column validated
 * against `FeatureFlagKeySchema` at the service boundary, not a Prisma enum, so
 * a new flag needs no schema change and a retired one leaves rows that resolve
 * to nothing rather than breaking a read.
 *
 * Removing a flag: delete the entry AND delete its rows in a migration. A row
 * whose key is no longer in the catalogue is unreachable - `listAccountFlags`
 * iterates the catalogue, not the table - so leaving it behind is invisible
 * debt, not a fallback.
 */
export const FEATURE_FLAG_KEYS = [
    'accounting',
    'entity-books',
    'principal-disbursement-vat',
    'statement-age-analysis',
];
export const FeatureFlagKeySchema = z.enum(FEATURE_FLAG_KEYS);
export const FEATURE_FLAGS = {
    accounting: {
        key: 'accounting',
        label: 'Accounting',
        description: 'The ledger, journals, bank accounts and financial reports, including accounting and bank-line allocation tools in chat for all account members. An administered entity inherits this from the firm administering it.',
        defaultEnabled: false,
    },
    'entity-books': {
        key: 'entity-books',
        label: 'Administered ledger',
        description: "Setting up a separate administered ledger for a legal entity the firm administers on a client's behalf, such as a business rescue or an insolvency. Also requires the Accounting flag.",
        defaultEnabled: false,
    },
    'principal-disbursement-vat': {
        key: 'principal-disbursement-vat',
        label: 'Principal disbursement VAT',
        description: "For a firm that bills disbursements as principal on its accountant's advice. An invoice line added from an expense whose supplier charged no VAT starts at the firm's own VAT rate instead of 0%; VAT the supplier did charge is still carried over exactly. Inert while the firm has no VAT number under its business information. The firm should also put Disbursements Recoverable on standard-rated purchases, or a bill from a VAT-registered supplier is charged VAT on VAT. Read on the account's own row only: an administered entity does not inherit it.",
        defaultEnabled: false,
    },
    'statement-age-analysis': {
        key: 'statement-age-analysis',
        label: 'Statement age analysis',
        description: "Adds an age analysis under the totals of a client's business and Unpaid invoices statement PDFs: what the client still owed at the statement date, by days past each invoice's due date, in the debtors age analysis report's buckets (Current, 1–30, 31–60, 61–90, 91–120 and 120+ days), any unallocated payments or credits (shown only when there are some), and the total due. Business statements and Unpaid invoices statements for a whole client change; Trust, Combined and matter statements stay as they are. A statement whose records do not add up to its closing balance is issued without the age analysis. Statements already saved or sent keep their original PDF. Read on the account's own row only: an administered entity does not inherit it.",
        defaultEnabled: false,
    },
};
/** The catalogue as a list, in a stable order for rendering. */
export const FEATURE_FLAG_DEFINITIONS = FEATURE_FLAG_KEYS.map((key) => FEATURE_FLAGS[key]);
/**
 * Resolve one flag from the catalogue default and the account's override.
 *
 * The single place the precedence rule lives, so the API read, the route gate
 * and the admin screen cannot disagree about what "no row" means.
 */
export function resolveFeatureFlag(key, override) {
    return override ?? FEATURE_FLAGS[key].defaultEnabled;
}
